/**
 * 规则变体配置（天凤 / 雀魂 预设可切换）
 *
 * 设计参考 RiichiEnv 的 `GameRule` + `default_tenhou()` / `default_mjsoul()`：
 *   · 一局一套规则，挂在 GameState 上 → 引擎各处（canApply / flow / score / detail）都能读到
 *   · 差异只在这些 flag 上，**行为实现共用一份代码**
 *
 * 天凤 vs 雀魂 的差异（来源：RiichiEnv docs/RULES.md）：
 *   | flag | 天凤 | 雀魂 |
 *   | 三家和(三家和了)      | 途中流局 | 三家和牌照算 |
 *   | 包牌(責任払い)        | 全额     | 只担包牌役满部分 |
 *   | 国士十三面待ち        | 单倍     | 双倍 |
 *   | 四暗刻单骑            | 单倍     | 双倍 |
 *   | 纯正九莲宝灯          | 单倍     | 双倍 |
 *   | 大四喜                | 单倍     | 双倍 |
 *   | 国士抢暗杠(chankan)   | 不允许   | 允许 |
 *   | 食い替え禁止          | 禁止     | 禁止 |
 */

import type { GameState } from './state.js';

export type RulePreset = 'tenhou' | 'mjsoul';

export interface RuleConfig {
  /** 预设名（仅用于显示/调试，行为由各 flag 决定） */
  readonly preset: RulePreset;

  /** 三家和（三家和了）：true = 途中流局（天凤），false = 三家和牌照算（雀魂） */
  readonly sanchahoIsDraw: boolean;

  /** 包牌（責任払い）：true = 只担包牌役满部分（雀魂），false = 承担全额（天凤） */
  readonly paoLiabilityOnly: boolean;

  /** 国士无双十三面待ち = 双倍役满 */
  readonly kokushi13MachiDouble: boolean;
  /** 四暗刻单骑 = 双倍役满 */
  readonly suuankouTankiDouble: boolean;
  /** 纯正九莲宝灯 = 双倍役满 */
  readonly junseiChuurenDouble: boolean;
  /** 大四喜 = 双倍役满 */
  readonly daisuushiiDouble: boolean;

  /** 允许国士无双抢暗杠（chankan） */
  readonly allowsRonOnAnkanForKokushi: boolean;

  /** 食い替え（打回刚吃/碰的同组牌）禁止 */
  readonly kuikaeForbidden: boolean;
}

/** 天凤预设（经典默认） */
export function defaultTenhou(): RuleConfig {
  return {
    preset: 'tenhou',
    sanchahoIsDraw: true,
    paoLiabilityOnly: false,
    kokushi13MachiDouble: false,
    suuankouTankiDouble: false,
    junseiChuurenDouble: false,
    daisuushiiDouble: false,
    allowsRonOnAnkanForKokushi: false,
    kuikaeForbidden: true,
  };
}

/** 雀魂预设 */
export function defaultMjSoul(): RuleConfig {
  return {
    preset: 'mjsoul',
    sanchahoIsDraw: false,
    paoLiabilityOnly: true,
    kokushi13MachiDouble: true,
    suuankouTankiDouble: true,
    junseiChuurenDouble: true,
    daisuushiiDouble: true,
    allowsRonOnAnkanForKokushi: true,
    kuikaeForbidden: true,
  };
}

/** 默认规则（未显式指定时使用天凤预设） */
export const DEFAULT_RULE: RuleConfig = defaultTenhou();

/**
 * 从状态取规则配置（兜底默认）
 *
 * ⚠️ `GameState.rules` 为可选字段 —— 既有测试夹具手动构造 GameState 时不必补它。
 */
export function rulesOf(state: GameState): RuleConfig {
  return state.rules ?? DEFAULT_RULE;
}

/** 预设名 → 配置 */
export function presetToRules(preset: RulePreset): RuleConfig {
  return preset === 'mjsoul' ? defaultMjSoul() : defaultTenhou();
}

/**
 * 「双倍役满」类役的倍数（依预设）
 *
 * 天凤：以下四役按**单倍**役满；雀魂：按**双倍**。
 * 供算点（yakumanCount）与包牌分摊使用。
 */
const DOUBLE_YAKUMAN_FLAG: Record<string, keyof RuleConfig> = {
  kokushi_13: 'kokushi13MachiDouble',
  suuankou_tanki: 'suuankouTankiDouble',
  junsei_chuuren: 'junseiChuurenDouble',
  daisuushii: 'daisuushiiDouble',
};

export function doubleYakumanMultiplier(id: string, rules: RuleConfig): number {
  const flag = DOUBLE_YAKUMAN_FLAG[id];
  if (!flag) return 1; // 非「双倍役满」类
  return rules[flag] ? 2 : 1;
}
